import React, { useContext } from "react";
import { HotelContext } from "../../../context/HotelContext";
import { getLocale, translate } from "../../../utils/Translation";
import LoadingIndicator from "./LoadingIndicator";
import "./ValueCard.css";

const ValueCard = ({
  value = 0,
  unit = "%",
  showUnit = true,
  variation = 0,
  badgeValue = null,
  badgeUnit = null,
  variationLabel = null,
  indicators = null,
  loading = false,
  title = "",
  subtitle = "",
  showVariationPercentage = true,
  className = "",
  valueColor = "#3C5544",
  badgePositiveColor = "#B0C996",
  badgeNegativeColor = "#CFA7A0",
  valueSize = "large",
}) => {
  const { lang } = useContext(HotelContext);
  const locale = getLocale(lang);
  const resolvedVariationLabel = variationLabel ?? translate("v2_vs_last_month", lang);

  if (loading) {
    return (
      <div className={`value-card ${className}`}>
        <div className="value-card__header">
          {title && <h3 className="value-card__title">{title}</h3>}
          {subtitle && <p className="value-card__subtitle">{subtitle}</p>}
        </div>
        <div className="value-card__content">
          <LoadingIndicator />
        </div>
      </div>
    );
  }

  const formattedValue = typeof value === "number" ? value.toLocaleString(locale) : value;
  const displayValue = showUnit && unit ? `${formattedValue}${unit}` : formattedValue;

  const hasMultipleIndicators = indicators && Array.isArray(indicators) && indicators.length > 0;

  const renderBadge = (indicatorValue, indicatorUnit, isPositive, badgeColor) => {
    const formattedIndicatorValue =
      typeof indicatorValue === "number" ? indicatorValue.toLocaleString(locale) : String(indicatorValue);
    const badgeDisplayValue = indicatorUnit ? `${formattedIndicatorValue}${indicatorUnit}` : formattedIndicatorValue;
    const finalBadgeColor = badgeColor || (isPositive ? badgePositiveColor : badgeNegativeColor);

    return (
      <div
        className={`value-card__badge ${isPositive ? "value-card__badge--positive" : "value-card__badge--negative"}`}
        style={{ backgroundColor: finalBadgeColor }}
      >
        {isPositive ? (
          <svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.5 1.5V7.5M1.5 4.5L4.5 1.5L7.5 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.5 7.5V1.5M1.5 4.5L4.5 7.5L7.5 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
        <span className="value-card__badge-value">{badgeDisplayValue}</span>
      </div>
    );
  };

  const renderSingleIndicator = () => {
    if (variation === undefined || variation === null) return null;

    const isPositive = variation >= 0;

    let badgeDisplayValue;
    if (badgeValue !== null && badgeValue !== undefined) {
      const formattedBadgeValue =
        typeof badgeValue === "number" ? badgeValue.toLocaleString(locale) : String(badgeValue);
      const badgeUnitToUse = badgeUnit !== null ? badgeUnit : showVariationPercentage ? "%" : "";
      badgeDisplayValue = badgeUnitToUse ? `${formattedBadgeValue}${badgeUnitToUse}` : formattedBadgeValue;
    } else {
      const variationValue = showVariationPercentage ? Math.abs(variation) : variation;
      const formattedVariation =
        typeof variationValue === "number" ? variationValue.toLocaleString(locale) : String(variationValue);
      const badgeUnitToUse = badgeUnit !== null ? badgeUnit : showVariationPercentage ? "%" : "";
      badgeDisplayValue = badgeUnitToUse
        ? `${formattedVariation}${badgeUnitToUse}`
        : (isPositive ? "+" : "") + formattedVariation;
    }

    return (
      <div className="value-card__indicator">
        {renderBadge(badgeDisplayValue, "", isPositive, null)}
        <span className="value-card__indicator-label">{resolvedVariationLabel}</span>
      </div>
    );
  };

  const renderMultipleIndicators = () => (
    <div className="value-card__indicators">
      {indicators.map((indicator, index) => (
        <div key={index} className="value-card__indicator">
          {renderBadge(
            indicator.value,
            indicator.unit || "",
            indicator.isPositive !== undefined ? indicator.isPositive : indicator.value >= 0,
            indicator.badgeColor
          )}
          <span className="value-card__indicator-label">{indicator.label}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className={`value-card ${className}`}>
      <div className="value-card__header">
        {title && <h3 className="value-card__title">{title}</h3>}
        {subtitle && <p className="value-card__subtitle">{subtitle}</p>}
      </div>
      <div className="value-card__content">
        <div className={`value-card__value value-card__value--${valueSize}`} style={{ color: valueColor }}>
          {displayValue}
        </div>
        {hasMultipleIndicators ? renderMultipleIndicators() : renderSingleIndicator()}
      </div>
    </div>
  );
};

export default ValueCard;
